import { useEffect, useRef } from 'react'
import type { Lang } from '../i18n'

export function MethodologyPanel({
  open,
  onClose,
  lang = 'en',
}: {
  open: boolean
  onClose: () => void
  lang?: Lang
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dlg = ref.current
    if (!dlg) return
    if (open && !dlg.open) dlg.showModal()
    else if (!open && dlg.open) dlg.close()
  }, [open])

  return (
    <dialog ref={ref} className="methodology-dialog" onClose={onClose}>
      <form method="dialog" className="methodology-close-form">
        <button type="submit" className="methodology-close" aria-label={lang === 'es' ? 'Cerrar' : 'Close'}>
          ×
        </button>
      </form>
      {lang === 'es' ? <MethodologyEs /> : <MethodologyEn />}
    </dialog>
  )
}

function MethodologyEn() {
  return (
    <article className="methodology-content">
        <h2>Methodology</h2>

        <section>
          <h3>BMR — basal metabolic rate</h3>
          <p>
            We use Mifflin-St Jeor (1990). Meta-analyses show it's roughly 10% more accurate
            for modern mixed populations than Harris-Benedict. When you provide a body-fat
            percentage, we switch to Katch-McArdle, which computes from lean body mass and
            is more accurate for athletic or heavier users.
          </p>
        </section>

        <section>
          <h3>NEAT — non-exercise activity thermogenesis</h3>
          <p>
            This is the single biggest source of error in traditional calculators. A
            multiplier like ×1.375 collapses occupation, daily steps, commute, and household
            activity into one coarse number. We separate them — your desk job, your 8,000
            steps, your 30-minute walk commute, and cooking three meals a day each produce
            their own range, and we sum them. Framework adapted from Levine (2002).
          </p>
        </section>

        <section>
          <h3>TEF — thermic effect of food</h3>
          <p>
            Digestion itself burns calories. Standard mixed diets land in the 8–10% range;
            high-protein diets push to 12–15%; low-carb sits around 9–11%. We adjust by your
            stated diet style and apply TEF to the sum of BMR + NEAT + EAT.
          </p>
        </section>

        <section>
          <h3>EAT — exercise activity thermogenesis</h3>
          <p>
            For deliberate exercise we use the Ainsworth 2011 Compendium of Physical
            Activities — the reference for MET values. Per-session burn = MET × 3.5 × kg /
            200 × minutes. An intensity envelope (light 0.85–1.0×, moderate 1.0–1.15×, hard
            1.1–1.25×) keeps the range honest when you're unsure how hard you went.
          </p>
        </section>

        <section>
          <h3>Why we return a range, not a single number</h3>
          <p>
            Every TDEE estimate has error bars. Traditional tools hide them behind a
            confident single number, which is misleading. The range is the honest answer.
          </p>
        </section>

        <section>
          <h3>Why traditional calculators fail in both directions</h3>
          <p>
            The activity-bucket dropdown quietly errs either way. Pick too-active a bucket
            and your TDEE is inflated — you think you're eating at a 500 kcal deficit but
            you're actually at maintenance, so the scale won't budge. Pick ×1.2 sedentary
            while you really walk 6,000 steps and cook dinner, and your TDEE is understated —
            you subtract 500 and land at a 700-plus kcal real deficit, steep enough that
            hunger collapses adherence within a week or two and ends in a weekend binge.
          </p>
          <p>
            Our decomposition is designed to land on <em>actual</em> maintenance, not a bucket
            approximation of it. The goal isn't a lower TDEE than traditional calculators —
            it's an accurate one.
          </p>
        </section>

        <section>
          <h3>Why the lower bound is recommended by default</h3>
          <p>
            Even a calibrated range has uncertainty. Eating at the lower bound survives
            roughly a 10% overestimate of your <em>own</em> activity inputs (self-reported
            steps, optimistic exercise intensity) and still creates a real deficit. Move to
            the midpoint or upper bound only after 2+ weeks of tracked intake and a confirmed
            weight trend.
          </p>
        </section>

        <section>
          <h3>What we don't do</h3>
          <ul>
            <li>No ads. No paid plans. No "premium" coach upsell.</li>
            <li>No email capture. No login or account required.</li>
          </ul>
        </section>

        <section>
          <h3>Sources</h3>
          <ul className="sources">
            <li>
              Mifflin MD, St Jeor ST, et al. A new predictive equation for resting energy
              expenditure in healthy individuals. <em>Am J Clin Nutr</em>. 1990;51(2):241–7.
            </li>
            <li>
              Katch FI, McArdle WD. <em>Introduction to Nutrition, Exercise, and Health</em>.
              4th ed. 1993.
            </li>
            <li>
              Ainsworth BE, et al. 2011 Compendium of Physical Activities. <em>Med Sci
              Sports Exerc</em>. 2011;43(8):1575–81.
            </li>
            <li>
              Levine JA. Non-exercise activity thermogenesis (NEAT). <em>Best Pract Res
              Clin Endocrinol Metab</em>. 2002;16(4):679–702.
            </li>
            <li>
              ACSM. <em>Guidelines for Exercise Testing and Prescription</em>. 11th ed. 2021.
            </li>
          </ul>
        </section>
      </article>
  )
}

function MethodologyEs() {
  return (
    <article className="methodology-content">
      <h2>Metodología</h2>

      <section>
        <h3>TMB — tasa metabólica basal</h3>
        <p>
          Usamos Mifflin-St Jeor (1990). Los metaanálisis muestran que es
          aproximadamente un 10% más precisa que Harris-Benedict para las
          poblaciones mixtas actuales. Cuando aportas un porcentaje de grasa
          corporal, cambiamos a Katch-McArdle, que calcula a partir de la masa
          magra y es más precisa para personas atléticas o con más peso.
        </p>
      </section>

      <section>
        <h3>NEAT — termogénesis por actividad sin ejercicio</h3>
        <p>
          Esta es la mayor fuente de error en las calculadoras tradicionales. Un
          multiplicador como ×1.375 colapsa la ocupación, los pasos diarios, el
          transporte y la actividad del hogar en un único número tosco. Nosotros
          los separamos — tu trabajo de escritorio, tus 8,000 pasos, tu trayecto
          a pie de 30 minutos y cocinar tres comidas al día producen cada uno su
          propio rango, y los sumamos. Marco adaptado de Levine (2002).
        </p>
      </section>

      <section>
        <h3>ETA — efecto térmico de los alimentos</h3>
        <p>
          La digestión en sí quema calorías. Las dietas mixtas estándar caen en
          el rango de 8–10%; las altas en proteína suben a 12–15%; las bajas en
          carbohidratos rondan el 9–11%. Ajustamos según tu estilo de dieta y
          aplicamos el ETA a la suma de TMB + NEAT + ejercicio.
        </p>
      </section>

      <section>
        <h3>Ejercicio — termogénesis por ejercicio</h3>
        <p>
          Para el ejercicio deliberado usamos el Compendio de Actividades Físicas
          de Ainsworth 2011 — la referencia de valores MET. Gasto por sesión =
          MET × 3.5 × kg / 200 × minutos. Un margen de intensidad (ligera
          0.85–1.0×, moderada 1.0–1.15×, fuerte 1.1–1.25×) mantiene el rango
          honesto cuando no estás seguro de cuánto te esforzaste.
        </p>
      </section>

      <section>
        <h3>Por qué devolvemos un rango y no un solo número</h3>
        <p>
          Toda estimación de TDEE tiene un margen de error. Las herramientas
          tradicionales lo esconden detrás de un único número confiado, lo cual
          es engañoso. El rango es la respuesta honesta.
        </p>
      </section>

      <section>
        <h3>Por qué las calculadoras tradicionales fallan en ambas direcciones</h3>
        <p>
          El menú de niveles de actividad se equivoca en cualquier dirección.
          Elige un nivel demasiado activo y tu TDEE se infla — crees que comes con
          un déficit de 500 kcal pero en realidad estás en mantenimiento, así que
          la báscula no se mueve. Elige ×1.2 sedentario cuando en realidad caminas
          6,000 pasos y cocinas la cena, y tu TDEE queda subestimado — restas 500 y
          terminas en un déficit real de más de 700 kcal, tan agresivo que el
          hambre rompe la adherencia en una o dos semanas y termina en un atracón
          de fin de semana.
        </p>
        <p>
          Nuestra descomposición está diseñada para acertar con el mantenimiento{' '}
          <em>real</em>, no con una aproximación de nivel. El objetivo no es un TDEE
          más bajo que las calculadoras tradicionales — es uno preciso.
        </p>
      </section>

      <section>
        <h3>Por qué se recomienda el límite inferior por defecto</h3>
        <p>
          Incluso un rango calibrado tiene incertidumbre. Comer en el límite
          inferior aguanta un sobreestimado de aproximadamente 10% de tus{' '}
          <em>propios</em> datos de actividad (pasos auto-reportados, intensidad de
          ejercicio optimista) y aun así crea un déficit real. Muévete al punto
          medio o al límite superior solo tras 2+ semanas de registro de ingesta y
          una tendencia de peso confirmada.
        </p>
      </section>

      <section>
        <h3>Lo que no hacemos</h3>
        <ul>
          <li>Sin anuncios. Sin planes de pago. Sin upsell de coach "premium".</li>
          <li>Sin captura de correo. Sin inicio de sesión ni cuenta.</li>
        </ul>
      </section>

      <section>
        <h3>Fuentes</h3>
        <ul className="sources">
          <li>
            Mifflin MD, St Jeor ST, et al. A new predictive equation for resting energy
            expenditure in healthy individuals. <em>Am J Clin Nutr</em>. 1990;51(2):241–7.
          </li>
          <li>
            Katch FI, McArdle WD. <em>Introduction to Nutrition, Exercise, and Health</em>.
            4th ed. 1993.
          </li>
          <li>
            Ainsworth BE, et al. 2011 Compendium of Physical Activities. <em>Med Sci
            Sports Exerc</em>. 2011;43(8):1575–81.
          </li>
          <li>
            Levine JA. Non-exercise activity thermogenesis (NEAT). <em>Best Pract Res
            Clin Endocrinol Metab</em>. 2002;16(4):679–702.
          </li>
          <li>
            ACSM. <em>Guidelines for Exercise Testing and Prescription</em>. 11th ed. 2021.
          </li>
        </ul>
      </section>
    </article>
  )
}
